import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from 'react-redux';
import type { AppState } from 'src/redux/store';
import { selectAllTopics } from 'src/redux/slices/topics';
import { TopicState } from 'src/types/state/topic';
import { useLazyListTopicsQuery } from 'src/redux/slices/api/su';
import { ROUTES } from 'src/constants/route';
import { useClientId, useSelLocale } from 'src/contexts/ClientContext';
import useMetrics from 'src/metrics/useUserMetrics';
import { FeatureName } from 'src/metrics/metricsFields';
import { AsyncDropdownButton } from '@amzn/seller-university-ui-components';
import { useBundle } from '@amzn/react-arb-tools';
import { BUNDLE_NAME, NAVIGATION_STRING_ID } from 'src/constants/localization';
import { t } from 'src/utils/translation';
import { getAppRoute } from 'src/utils/routing';

type MenuOption = {
  label: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
};

/** Error content: emphasize text without affecting document heading semantics */
const TopicsDropdownErrorContent: React.FC = () => {
  const [navigationBundle] = useBundle(BUNDLE_NAME.NAVIGATION);
  return (
    <div>
      <div className="font-semibold text-su-text-primary">
        <strong>
          {t(navigationBundle, NAVIGATION_STRING_ID.DROPDOWN_BUTTON_ERROR_HEADER)}
        </strong>
      </div>
      <div className="py-su-space-02">
        {t(navigationBundle, NAVIGATION_STRING_ID.DROPDOWN_BUTTON_ERROR_DESCRIPTION)}
      </div>
    </div>
  );
};

const TopicsDropdownNoOptions: React.FC = () => {
  const [navigationBundle] = useBundle(BUNDLE_NAME.NAVIGATION);
  return (
    <div className="py-su-space-02">
      {t(navigationBundle, NAVIGATION_STRING_ID.DROPDOWN_BUTTON_NO_OPTIONS)}
    </div>
  );
};

const TopicsDropdownButton: React.FC = () => {
  const [navigationBundle] = useBundle(BUNDLE_NAME.NAVIGATION);
  const navigate = useNavigate();
  const store = useStore<AppState>();
  const clientId = useClientId();
  const selLocale = useSelLocale();
  const { clickMetric } = useMetrics();

  const buildTopicUrl = useCallback(
    (topicId: string) => getAppRoute(`${ROUTES.LEARN.BASE}/topic/${topicId}`),
    [],
  );

  const mapTopicsToOptions = useCallback(
    (topics: Array<{ topicId: string; title: string }>): MenuOption[] => topics
      // Basic defensive check: ensure required fields exist
      .filter((topic) => topic?.topicId && topic?.title)
      .map(({ topicId, title }) => ({
        label: title,
        onClick: () => navigate(buildTopicUrl(topicId)),
      })),
    [navigate, buildTopicUrl],
  );

  // Use lazy query to let RTK Query handle caching and deduplication automatically
  const [triggerListTopics] = useLazyListTopicsQuery();

  const handleExpand = useCallback(async (): Promise<MenuOption[]> => {
    clickMetric({ feature: FeatureName.NavTopicsDropdownOpen });
    await triggerListTopics({ clientId, selLocale }, true).unwrap();

    const topics = selectAllTopics(store.getState()) || [];

    return mapTopicsToOptions(
      topics.map((topic: TopicState) => ({ topicId: topic.topicId, title: topic.title })),
    );
  }, [triggerListTopics, store, mapTopicsToOptions, clientId, selLocale, clickMetric]);

  return (
    <AsyncDropdownButton
      id="topics-dropdown"
      position="bottom"
      className="[font-family:inherit] [&_button]:[font-family:inherit] [&_span]:[font-family:inherit]"
      triggerProps={{
        onExpand: handleExpand,
        triggerLabel: t(navigationBundle, NAVIGATION_STRING_ID.DROPDOWN_BUTTON_TOPICS),
      }}
      menuLayoutProps={{
        ErrorContent: TopicsDropdownErrorContent,
        noOptionsComponent: TopicsDropdownNoOptions,
        className: 'flex flex-col [&_button]:block',
      }}
    />
  );
};

export default TopicsDropdownButton;
